"use client";

import { Control, Controller } from "react-hook-form";

import { OptionType, SpotSelect } from "@/components/Form/SelectField";
import { ExpandableTextarea } from "@/components/Assistant/ExpandableTextarea";
import { cn } from "@/lib/utils";

interface GuardrailCardValue {
  value: string;
  context: string;
}

interface GuardrailCardProps {
  control: Control<any>;
  name: string;
  label: string;
  values: string[];
  offValue?: string;
  contextPlaceholder?: string;
  accordionLabel?: string;
  isDisabled?: boolean;
}

export function GuardrailCard({
  control,
  name,
  label,
  values,
  offValue = "Off",
  contextPlaceholder,
  accordionLabel = "Additional context",
  isDisabled,
}: GuardrailCardProps) {
  const options: OptionType[] = values.map((v) => ({ label: v, value: v }));

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={offValue}
      render={({ field }) => {
        const current = field.value as GuardrailCardValue | string | undefined;
        const level =
          typeof current === "string" ? current : (current?.value ?? offValue);
        const context =
          typeof current === "string" ? "" : (current?.context ?? "");
        const showContext = level !== offValue;
        const update = (patch: Partial<GuardrailCardValue>) => {
          field.onChange({ value: level, context, ...patch });
        };

        return (
          <div
            className={cn(
              "flex flex-col w-full overflow-hidden",
              showContext && "border border-border rounded-lg"
            )}
          >
            <div
              className={cn(
                "flex items-center justify-between gap-3",
                showContext && "p-2 pl-3"
              )}
            >
              <span className="text-sm font-medium">{label}</span>
              <div className="w-40 shrink-0">
                <SpotSelect
                  name={name}
                  options={options}
                  value={level}
                  isDisabled={isDisabled}
                  isNotSearchable
                  onBlur={field.onBlur}
                  onChange={(option: OptionType | null) =>
                    update({ value: option?.value ?? offValue })
                  }
                />
              </div>
            </div>

            {showContext && (
              <ExpandableTextarea
                value={context}
                onChange={(next) => {
                  const text =
                    typeof next === "string"
                      ? next
                      : ((next as { text?: string })?.text ?? "");
                  update({ context: text });
                }}
                isDisabled={isDisabled}
                label={accordionLabel}
                placeholder={contextPlaceholder}
              />
            )}
          </div>
        );
      }}
    />
  );
}
